import React, { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { mockVendorManagementService } from '@/services/mock/MockVendorManagementService';
import { VendorFilterState, VendorMetricsSummary, VendorRecord, VendorStatusKey } from '@/types/domain/vendor-management';
import { VendorKpiCards } from '@/components/vendors/VendorKpiCards';
import { DataTable, ColumnDef } from '@/components/data-display/DataTable';
import { VendorActionModal } from '@/components/vendors/VendorActionModal';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import {
  Plus,
  RefreshCw,
  Search,
  Users,
  Eye,
  Pencil,
  Trash2,
  Building2,
  ShieldCheck,
  ShieldAlert,
  Star,
  AlertTriangle,
  Loader2,
  X,
} from 'lucide-react';
import { ROUTES } from '@/constants/routes';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { SlideOverDrawer } from '@/components/ui/SlideOverDrawer';
import { VendorDetailPage } from './VendorDetailPage';

const renderVendorStatus = (status: VendorStatusKey) => {
  let color = 'bg-primary/10 text-primary border-primary/20';
  let dot = 'bg-primary';

  switch (status) {
    case 'APPROVED':
    case 'ACTIVE':
      color = 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20';
      dot = 'bg-emerald-500';
      break;
    case 'PENDING_REVIEW':
      color = 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20';
      dot = 'bg-amber-500';
      break;
    case 'SUSPENDED':
      color = 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20';
      dot = 'bg-orange-500';
      break;
    case 'BLOCKED':
      color = 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20';
      dot = 'bg-rose-500';
      break;
  }

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border shadow-2xs font-poppins whitespace-nowrap w-32 justify-center ${color}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${dot} shrink-0`} />
      <span className="truncate">{status.replace(/_/g, ' ')}</span>
    </span>
  );
};

export const VendorsListPage: React.FC = () => {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState<VendorMetricsSummary | null>(null);

  // Status Change Modal State
  const [selectedVendorForAction, setSelectedVendorForAction] = useState<VendorRecord | null>(null);
  const [actionType, setActionType] = useState<'APPROVE' | 'SUSPEND' | 'BLOCK' | 'REJECT'>('SUSPEND');
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [isSubmittingAction, setIsSubmittingAction] = useState(false);

  // Deletion Modal State
  const [vendorToDelete, setVendorToDelete] = useState<VendorRecord | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedVendorForDrawer, setSelectedVendorForDrawer] = useState<string | null>(null);

  const [filterState, setFilterState] = useState<VendorFilterState>({
    search: '',
    status: 'ALL',
    category: 'ALL',
    industry: 'ALL',
    verificationStatus: 'ALL',
    riskRating: 'ALL',
    page: 1,
    pageSize: 25,
  });

  const fetchMetrics = useCallback(async () => {
    const m = await mockVendorManagementService.getVendorMetrics();
    setMetrics(m);
  }, []);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (!vendorToDelete && !isActionModalOpen) return;
    const orig = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = orig;
    };
  }, [vendorToDelete, isActionModalOpen]);

  const fetchFn = useCallback(
    async (params: { page?: number; pageSize?: number }) => {
      const res = await mockVendorManagementService.getVendorRecords({
        ...filterState,
        page: params.page || 1,
        pageSize: params.pageSize || 25,
      });
      return {
        data: res.items,
        page: params.page || 1,
        pageSize: params.pageSize || 25,
        total: res.total,
        totalPages: Math.ceil(res.total / (params.pageSize || 25)),
        hasNextPage: (params.page || 1) * (params.pageSize || 25) < res.total,
        hasPreviousPage: (params.page || 1) > 1,
      };
    },
    [filterState]
  );

  const {
    items: records,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    total,
    sentinelRef,
    retry,
  } = useInfiniteScroll<VendorRecord>({
    fetchFn,
    params: { ...filterState },
    pageSize: 25,
  });

  const handleConfirmStatusChange = async (newStatus: VendorStatusKey, reason: string) => {
    if (!selectedVendorForAction) return;
    try {
      setIsSubmittingAction(true);
      await mockVendorManagementService.updateVendorStatus(selectedVendorForAction.id, newStatus, reason);
      setIsActionModalOpen(false);
      setSelectedVendorForAction(null);
      retry();
      fetchMetrics();
    } finally {
      setIsSubmittingAction(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!vendorToDelete) return;
    const targetId = vendorToDelete.id;
    try {
      setIsDeleting(true);
      await mockVendorManagementService.deleteVendor(targetId);
      setVendorToDelete(null);
      retry();
      fetchMetrics();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to delete vendor record.');
    } finally {
      setIsDeleting(false);
    }
  };

  const columns: ColumnDef<VendorRecord>[] = [
    {
      header: 'Vendor Master ID',
      accessorKey: 'vendorCode',
      sortable: true,
      cell: (row) => (
        <div className="space-y-0.5 font-poppins">
          <span
            onClick={() => navigate(ROUTES.VENDORS.DETAILS(row.id))}
            className="font-poppins font-bold text-primary hover:underline cursor-pointer whitespace-nowrap text-sm block"
          >
            {row.vendorCode}
          </span>
          <span className="text-xs text-foreground-muted font-mono block whitespace-nowrap">
            {row.sapVendorCode ? `SAP: ${row.sapVendorCode}` : 'SAP: Non-Synced'} • {row.vendorType}
          </span>
        </div>
      ),
    },
    {
      header: 'Supplier & Contact',
      accessorKey: 'name',
      sortable: true,
      cell: (row) => (
        <div className="flex flex-col min-w-0 max-w-[200px] xl:max-w-[240px] font-poppins">
          <span
            className="font-bold text-foreground text-sm truncate block"
            title={row.name}
          >
            {row.name}
          </span>
          <span
            className="text-xs text-foreground-muted font-mono truncate block"
            title={row.primaryContact.email}
          >
            {row.primaryContact.name} • {row.primaryContact.email}
          </span>
        </div>
      ),
    },
    {
      header: 'Category & Industry',
      accessorKey: 'category',
      cell: (row) => (
        <div className="max-w-[130px] xl:max-w-[160px] truncate font-poppins" title={row.category}>
          <span className="text-sm font-semibold text-foreground truncate block flex items-center gap-1">
            <Building2 className="h-3.5 w-3.5 text-primary shrink-0" />
            {row.category}
          </span>
          <span className="text-xs text-foreground-muted truncate block">
            {row.industry}
          </span>
        </div>
      ),
    },
    {
      header: 'Performance & Risk',
      cell: (row) => (
        <div className="font-poppins space-y-1 whitespace-nowrap">
          <div className="flex items-center gap-1.5 font-bold text-xs text-foreground">
            <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-500" />
            <span>{row.performance.performanceScore} / 100</span>
          </div>
          <div>
            {row.riskRating === 'LOW' ? (
              <span className="text-emerald-600 dark:text-emerald-400 font-semibold text-[11px] flex items-center gap-1">
                <ShieldCheck className="h-3 w-3" /> Low Risk
              </span>
            ) : (
              <span className="text-rose-600 dark:text-rose-400 font-semibold text-[11px] flex items-center gap-1">
                <ShieldAlert className="h-3 w-3" /> {row.riskRating} Risk
              </span>
            )}
          </div>
        </div>
      ),
    },
    {
      header: 'Status',
      accessorKey: 'status',
      cell: (row) => (
        <div className="flex items-center font-poppins">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedVendorForAction(row);
              setActionType(row.status === 'SUSPENDED' ? 'APPROVE' : 'SUSPEND');
              setIsActionModalOpen(true);
            }}
            title="Click to review/update status"
            className="cursor-pointer hover:opacity-80 transition-opacity"
          >
            {renderVendorStatus(row.status)}
          </button>
        </div>
      ),
    },
    {
      header: 'Actions',
      cell: (row) => (
        <div
          className="flex items-center gap-1.5 justify-start shrink-0 whitespace-nowrap font-poppins min-w-[110px]"
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedVendorForDrawer(row.id)}
            title="View Vendor Profile (Slide-over)"
            className="h-8 w-8 p-0 rounded-lg hover:bg-primary/15 hover:text-primary active:scale-90 transition-all duration-200 cursor-pointer text-foreground-secondary shrink-0 inline-flex items-center justify-center group shadow-2xs hover:shadow-xs"
          >
            <Eye className="h-4 w-4 transition-transform duration-200 group-hover:scale-115" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(ROUTES.VENDORS.EDIT(row.id))}
            title="Edit Vendor Details"
            className="h-8 w-8 p-0 rounded-lg hover:bg-primary/10 cursor-pointer text-primary hover:text-primary-dark shrink-0 inline-flex items-center justify-center"
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setVendorToDelete(row)}
            title="De-list Vendor"
            className="h-8 w-8 p-0 rounded-lg text-rose-500 hover:text-rose-600 hover:bg-rose-500/15 cursor-pointer shrink-0 inline-flex items-center justify-center"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6 pb-12 font-poppins">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-foreground flex items-center gap-2.5 font-poppins">
            <Users className="h-7 w-7 text-primary" /> Vendor Master & Compliance Directory
          </h1>
          <p className="text-xs text-foreground-secondary font-medium mt-1 font-poppins">
            Enterprise supplier master database, SAP vendor synchronization, performance scorecards, and statutory compliance.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              fetchMetrics();
              retry();
            }}
            className="rounded-xl font-bold font-poppins cursor-pointer"
          >
            <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
          </Button>

          <Button
            onClick={() => navigate(ROUTES.VENDORS.NEW)}
            className="rounded-xl font-bold bg-primary hover:bg-primary/90 text-primary-foreground font-poppins cursor-pointer shadow-xs"
          >
            <Plus className="h-4 w-4 mr-1.5" /> Onboard Vendor
          </Button>
        </div>
      </div>

      {/* KPI Cards Strip */}
      {metrics && (
        <VendorKpiCards
          metrics={metrics}
          onFilterClick={(statusGroup) => {
            setFilterState((prev) => ({
              ...prev,
              status: statusGroup === 'ALL' ? 'ALL' : statusGroup,
            }));
          }}
        />
      )}

      {/* Main Ledger Card with Squeezed Status Tabs & Search */}
      <Card className="border-border rounded-2xl shadow-xs overflow-hidden font-poppins">
        <CardHeader className="p-4 border-b border-border/60 bg-muted/20">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            {/* Squeezed Status Tabs */}
            <div className="flex flex-wrap items-center gap-1.5">
              {[
                { key: 'ALL', label: 'All Vendors' },
                { key: 'APPROVED', label: 'Approved' },
                { key: 'ACTIVE', label: 'Active' },
                { key: 'PENDING_REVIEW', label: 'Pending Review' },
                { key: 'SUSPENDED', label: 'Suspended' },
                { key: 'BLOCKED', label: 'Blocked' },
              ].map((tab) => {
                const isActive = filterState.status === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setFilterState((prev) => ({ ...prev, status: tab.key }))}
                    className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all cursor-pointer font-poppins ${
                      isActive
                        ? 'bg-primary text-primary-foreground font-bold shadow-xs'
                        : 'bg-card border border-border text-foreground-secondary hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Search Input */}
            <div className="relative min-w-[240px]">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground-muted" />
              <input
                type="text"
                value={filterState.search}
                onChange={(e) => setFilterState((prev) => ({ ...prev, search: e.target.value }))}
                placeholder="Search vendor code, name, category..."
                className="w-full h-9 pl-9 pr-3 text-xs font-medium rounded-xl border border-input bg-background text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-1 focus:ring-primary font-poppins"
              />
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-4 overflow-x-auto">
          <DataTable
            data={records}
            columns={columns}
            isLoading={isLoading}
            pageSize={25}
            totalCount={total}
            infiniteScroll={true}
            isFetchingNextPage={isFetchingNextPage}
            hasNextPage={hasNextPage}
            sentinelRef={sentinelRef}
            searchPlaceholder="Search vendor directory..."
            fixedHeight={true}
            containerHeightClass="max-h-[580px] overflow-y-auto overflow-x-auto custom-scrollbar"
            emptyTitle="No Vendors Found"
            emptyDescription="There are no supplier master records matching your current filter criteria."
            onRowClick={(row) => navigate(ROUTES.VENDORS.DETAILS(row.id))}
          />
        </CardContent>
      </Card>

      {/* Status Action Modal (Suspend / Block / Approve) */}
      {selectedVendorForAction && (
        <VendorActionModal
          isOpen={isActionModalOpen}
          onClose={() => setIsActionModalOpen(false)}
          vendor={selectedVendorForAction}
          targetAction={actionType}
          isSubmitting={isSubmittingAction}
          onConfirm={handleConfirmStatusChange}
        />
      )}

      {/* Delete Confirmation Modal (Mounted via createPortal to document.body) */}
      {vendorToDelete &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 backdrop-blur-sm p-4 animate-in fade-in"
            onClick={() => !isDeleting && setVendorToDelete(null)}
          >
            <div
              className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-2xl space-y-4 font-poppins animate-in zoom-in-95 my-auto text-foreground"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-border/60 pb-3">
                <div className="flex items-center gap-2 text-rose-600">
                  <AlertTriangle className="h-5 w-5" />
                  <h3 className="text-base font-bold text-foreground font-poppins">
                    De-list Vendor Master Record?
                  </h3>
                </div>
                <button
                  onClick={() => !isDeleting && setVendorToDelete(null)}
                  className="rounded-lg p-1 hover:bg-muted text-foreground-muted cursor-pointer"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Vendor Details Box */}
              <div className="rounded-xl bg-muted/40 border border-border p-3.5 space-y-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-foreground-muted uppercase tracking-wider text-[10px]">
                    Vendor Code
                  </span>
                  <span className="font-mono font-bold text-primary">
                    {vendorToDelete.vendorCode}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-foreground-muted uppercase tracking-wider text-[10px]">
                    Trade Name
                  </span>
                  <span className="font-bold text-foreground truncate max-w-[200px] text-right">
                    {vendorToDelete.name}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-foreground-muted uppercase tracking-wider text-[10px]">
                    Category
                  </span>
                  <span className="font-bold text-foreground">
                    {vendorToDelete.category}
                  </span>
                </div>
              </div>

              <p className="text-xs text-foreground-secondary leading-relaxed">
                Are you sure you want to de-list this vendor from the enterprise master directory? Active purchase orders and open RFQ bids from this vendor will be flagged.
              </p>

              <div className="flex items-center justify-end gap-3 pt-2 border-t border-border/60">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isDeleting}
                  onClick={() => setVendorToDelete(null)}
                  className="cursor-pointer font-bold rounded-xl font-poppins"
                >
                  Cancel
                </Button>
                <button
                  type="button"
                  disabled={isDeleting}
                  onClick={handleConfirmDelete}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 active:scale-95 text-white font-extrabold text-xs shadow-lg shadow-rose-600/30 transition-all cursor-pointer disabled:opacity-50 font-poppins"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      <span>De-listing...</span>
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-3.5 w-3.5" />
                      <span>De-list Vendor</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Right Slide-Over Curtain Drawer for Vendor Profile */}
      <SlideOverDrawer
        isOpen={Boolean(selectedVendorForDrawer)}
        onClose={() => setSelectedVendorForDrawer(null)}
        title="Vendor Master Profile Dossier"
        subtitle="Commercial master data, statutory compliance, quality ratings, and active RFQ sourcing"
        fullPageUrl={selectedVendorForDrawer ? ROUTES.VENDORS.DETAILS(selectedVendorForDrawer) : undefined}
      >
        {selectedVendorForDrawer && (
          <VendorDetailPage
            recordId={selectedVendorForDrawer}
            isDrawer={true}
            onClose={() => setSelectedVendorForDrawer(null)}
          />
        )}
      </SlideOverDrawer>
    </div>
  );
};
